/**
 * useCrmSettings — fetches and caches CRM feature flag + settings per account.
 *
 * Usage: call once at app boot (e.g. in Sidebar.vue) and inject into any
 * component that needs `enabled` or `waitClockSeconds`.
 *
 * Guarantees:
 *  - Refs always reflect the CURRENT account — reset synchronously on every
 *    account switch before the async fetch resolves (no cross-account leak).
 *  - Fetch ordering guard: stale responses from a previous account are discarded.
 *  - Only one in-flight request at a time per account (deduplicated).
 *  - Never throws — 401/network errors degrade gracefully to fail-closed defaults.
 *  - localStorage hydration: last-known state per account for instant first paint.
 *
 * GET /api/v1/accounts/:id/cw_custom/crm_settings
 * Response: { enabled: bool, wait_clock_seconds: int, internal_inbox_ids: int[] }
 */
/* eslint-disable no-underscore-dangle -- os refs `_xxx` abaixo são singletons
   PRIVADOS do módulo (estado compartilhado, exposto só via computed getters no
   retorno do composable). O underscore marca essa privacidade de propósito; a
   regra airbnb conflita com essa convenção intencional, então desabilitamos só
   neste arquivo. */
import { ref, computed, watch } from 'vue';
import crmBoardApi from 'dashboard/api/cw_custom/crmBoard.js';

// localStorage key prefix — scoped by accountId to support multi-account.
const LS_KEY = id => `cw_custom:crm_settings:${id}`;

// Module-level cache keyed by accountId (in-memory, per session).
const cache = new Map(); // accountId → { enabled, waitClockSeconds, internalInboxIds }
const pending = new Set(); // accountIds currently being fetched

// Singleton refs — shared reactively but always reset to the CURRENT account's
// known state before any async work (see applyState below).
const _enabled = ref(false);
const _waitClockSeconds = ref(120);
const _internalInboxIds = ref([]);
// custom (2026-08-09): a aba NOTAS, por conta. Segue a MESMA disciplina de `_enabled` —
// é uma flag de visibilidade, então falha FECHADA em qualquer erro. Ver a nota no catch.
const _notasEnabled = ref(false);
// custom (2026-08-09, Lote 2): a aba NOTIFICAÇÕES. Mesma disciplina — falha FECHADA.
const _notificacoesEnabled = ref(false);
// custom (2026-08-09, Lote 3): a aba FLUXOS (admin-only, somada ao papel na sidebar).
const _fluxosEnabled = ref(false);
const _loaded = ref(false);

// Normalize the internal_inbox_ids payload to an array of integers. Tolerates
// jsonb string ids ("6") and a missing/non-array field (fail-closed to []).
function normalizeInboxIds(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map(Number).filter(Number.isInteger);
}

// ---------- localStorage helpers ----------

function readStorage(id) {
  try {
    const raw = localStorage.getItem(LS_KEY(id));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed.enabled !== 'boolean') return null;
    return {
      enabled: parsed.enabled,
      waitClockSeconds: Number(parsed.waitClockSeconds) || 120,
      internalInboxIds: normalizeInboxIds(parsed.internalInboxIds),
      notasEnabled: parsed.notasEnabled === true,
      notificacoesEnabled: parsed.notificacoesEnabled === true,
      fluxosEnabled: parsed.fluxosEnabled === true,
    };
  } catch {
    return null;
  }
}

function writeStorage(id, settings) {
  try {
    localStorage.setItem(LS_KEY(id), JSON.stringify(settings));
  } catch {
    // localStorage full or blocked (private mode) — silently skip.
  }
}

// ---------- core ----------

/**
 * Synchronously set the singleton refs to the best-known state for `id`.
 * Called at the START of every load so refs never hold a previous account's
 * value while the fetch is in flight.
 *
 * Priority: in-memory cache → localStorage → fail-closed defaults.
 *
 * @param {string} id
 */
function applyState(id) {
  if (cache.has(id)) {
    const c = cache.get(id);
    _enabled.value = c.enabled;
    _waitClockSeconds.value = c.waitClockSeconds;
    _internalInboxIds.value = c.internalInboxIds;
    _notasEnabled.value = c.notasEnabled;
    _notificacoesEnabled.value = c.notificacoesEnabled;
    _fluxosEnabled.value = c.fluxosEnabled;
    _loaded.value = true;
    return;
  }

  const stored = readStorage(id);
  if (stored) {
    // Hydrate from localStorage for a flash-free first paint.
    // _loaded stays false until the server confirms.
    _enabled.value = stored.enabled;
    _waitClockSeconds.value = stored.waitClockSeconds;
    _internalInboxIds.value = stored.internalInboxIds;
    _notasEnabled.value = stored.notasEnabled;
    _notificacoesEnabled.value = stored.notificacoesEnabled;
    _fluxosEnabled.value = stored.fluxosEnabled;
  } else {
    // No known state — fail-closed so the link never appears for the wrong account.
    _enabled.value = false;
    _waitClockSeconds.value = 120;
    _internalInboxIds.value = [];
    _notasEnabled.value = false;
    _notificacoesEnabled.value = false;
    _fluxosEnabled.value = false;
  }
  // _loaded stays false — caller knows we're still fetching.
}

/**
 * Fetches settings for `accountId`, always resetting refs synchronously first.
 *
 * Takes the full accountIdRef (not just the value snapshot) so ordering guards
 * can compare against the CURRENT ref value when the async response arrives.
 *
 * @param {import('vue').Ref<number|string>} accountIdRef
 */
async function loadSettings(accountIdRef) {
  const id = String(accountIdRef.value);
  if (!id || id === 'undefined') return;

  // 1. Synchronous reset: refs now reflect THIS account's best-known state.
  //    Eliminates the cross-account value leak on every switch.
  applyState(id);

  // 2. Cache hit — applyState already applied it and set _loaded=true.
  if (cache.has(id)) return;

  // 3. Dedup — already fetching this account.
  if (pending.has(id)) return;
  pending.add(id);

  try {
    const res = await crmBoardApi.fetchCrmSettings(id);

    // Ordering guard: discard if the user switched accounts while this was in flight.
    if (id !== String(accountIdRef.value)) return;

    const data = res.data ?? {};
    const settings = {
      enabled: Boolean(data.enabled),
      // custom (Modeloja · Lote 9): 120 → 48h, o mesmo padrão do servidor e do composable
      // do relógio. Três lugares guardavam este número; deixar um para trás faria a tela
      // discordar de si mesma dependendo de qual caminho respondesse primeiro.
      waitClockSeconds: Number(data.wait_clock_seconds) || 48 * 60 * 60,
      internalInboxIds: normalizeInboxIds(data.internal_inbox_ids),
      notasEnabled: Boolean(data.notas_enabled),
      notificacoesEnabled: Boolean(data.notificacoes_enabled),
      fluxosEnabled: Boolean(data.fluxos_enabled),
    };
    cache.set(id, settings);
    writeStorage(id, settings);

    // Guard again before writing reactive refs (two awaits could interleave).
    if (id !== String(accountIdRef.value)) return;
    _enabled.value = settings.enabled;
    _waitClockSeconds.value = settings.waitClockSeconds;
    _internalInboxIds.value = settings.internalInboxIds;
    _notasEnabled.value = settings.notasEnabled;
    _notificacoesEnabled.value = settings.notificacoesEnabled;
    _fluxosEnabled.value = settings.fluxosEnabled;
  } catch {
    if (id !== String(accountIdRef.value)) return;
    // Visibility flag: always fail-closed on fetch failure — never trust a stale
    // enabled:true from localStorage when the server didn't confirm it this session.
    // A blip that hides the link is the correct side to err on for a security gate.
    _enabled.value = false;
    // Mesma razão de `_enabled`: é flag de visibilidade de navegação, então falha FECHADA.
    // Um item de menu que aparece por causa de uma falha de rede leva a uma tela que
    // responde 404 — pior que o item não existir.
    _notasEnabled.value = false;
    _notificacoesEnabled.value = false;
    _fluxosEnabled.value = false;
    // waitClockSeconds is a non-security threshold — keep the stale value if present
    // (inoffensive, and avoids resetting a custom threshold on transient errors).
    const stored = readStorage(id);
    if (!stored) _waitClockSeconds.value = 120;
    // internalInboxIds gates a visual hide. On error, fail OPEN (hide nothing) so a
    // transient blip never makes a customer inbox vanish from the Channels list.
    _internalInboxIds.value = stored ? stored.internalInboxIds : [];
  } finally {
    pending.delete(id);
    if (id === String(accountIdRef.value)) _loaded.value = true;
  }
}

/**
 * Composable: returns reactive settings for the given accountId ref.
 * Watches accountId and reloads (with synchronous reset) when it changes.
 *
 * @param {import('vue').Ref<number|string>} accountIdRef
 */
export function useCrmSettings(accountIdRef) {
  // Pass the full ref so ordering guards work correctly inside loadSettings.
  watch(
    accountIdRef,
    id => {
      if (id) loadSettings(accountIdRef);
    },
    { immediate: true }
  );

  return {
    crmEnabled: computed(() => _enabled.value),
    waitClockSeconds: computed(() => _waitClockSeconds.value),
    // Internal inbox ids (e.g. "Equipe Modeloja") — used to hide the internal inbox
    // from the sidebar Channels list. Independent of crmEnabled.
    internalInboxIds: computed(() => _internalInboxIds.value),
    // custom (2026-08-09): gate do item "Notas" na sidebar. Flag por conta, off por padrão.
    notasEnabled: computed(() => _notasEnabled.value),
    // custom (2026-08-09): gate do item "Notificações" na sidebar.
    notificacoesEnabled: computed(() => _notificacoesEnabled.value),
    // custom (2026-08-09): gate do item "Fluxos" (somado a isAdmin na sidebar).
    fluxosEnabled: computed(() => _fluxosEnabled.value),
    // true once the authoritative server response has resolved for the current account.
    crmSettingsLoaded: computed(() => _loaded.value),
  };
}
